import React, { useEffect, useRef, useState } from "react";
import {
  FaVideo,
  FaCalendarAlt,
  FaClock,
  FaSearch,
  FaBookOpen,
  FaTrash,
  FaEdit,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaChalkboardTeacher,
  FaExternalLinkAlt,
  FaFilter,
  FaSyncAlt,
  FaExclamationCircle
} from "react-icons/fa";
import { useSelector } from "react-redux";
import axiosInstance from "../../../axios";
import { Loading } from "../../Other/Loading";
import Modal from "../../Other/Modal";

const InstructorClasses = () => {
  const user = useSelector((state) => state.users.user);
  const instructorId = user?.userId || user?.id;
  const isAdmin = user?.role === 1 || user?.role === "1";

  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [courseFilter, setCourseFilter] = useState("all");

  // Google Calendar integration states
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Searchable course dropdown states
  const [courseSearch, setCourseSearch] = useState("");
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const courseDropdownRef = useRef(null);

  // Modal States
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [isCourseLocked, setIsCourseLocked] = useState(false);
  const [existingScheduleWarning, setExistingScheduleWarning] = useState(false);

  // Form Fields
  const [formData, setFormData] = useState({
    course_id: "",
    class_title: "",
    class_description: "",
    start_date: "",
    start_time: "",
    end_time: "",
    google_meet_link: ""
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      let url = "";
      if (isAdmin) {
        url = "/schedule"; // Get all classes
      } else {
        url = `/schedule/instructor/${instructorId}`;
      }
      
      const response = await axiosInstance.get(url);
      if (response.data && response.data.success) {
        setSchedules(response.data.data || []);
        setFilteredSchedules(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching schedules:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axiosInstance.get("/schedule/courses");
      if (response.data && response.data.success) {
        setCourses(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching dropdown courses:", err);
    }
  };

  const checkGoogleStatus = async () => {
    try {
      const response = await axiosInstance.get("/schedule/google/status");
      if (response.data && response.data.success) {
        setIsGoogleLinked(response.data.isLinked);
      }
    } catch (err) {
      console.error("Error checking Google link status:", err);
    }
  };

  const handleLinkGoogle = async () => {
    setGoogleLoading(true);
    try {
      const response = await axiosInstance.get("/schedule/google/auth-url");
      if (response.data && response.data.url) {
        window.open(response.data.url, "_blank", "width=600,height=700");
      }
    } catch (err) {
      console.error("Error initiating Google OAuth:", err);
      alert("Failed to connect with Google Calendar. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    if (instructorId) {
      fetchSchedules();
      fetchCourses();
      checkGoogleStatus();
    }
  }, [instructorId, isAdmin]);

  useEffect(() => {
    const handleFocus = () => {
      if (instructorId) {
        checkGoogleStatus();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [instructorId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(e.target)) {
        setCourseDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format Helper
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "N/A";
    const d = new Date(timeStr);
    return isNaN(d) ? timeStr : d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Extract date string YYYY-MM-DD in local time
  const getYYYYMMDD = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Extract time string HH:mm in local time
  const getHHMM = (timeStr) => {
    if (!timeStr) return "";
    const d = new Date(timeStr);
    if (isNaN(d)) return "";
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  // Check if meeting is accessible (within 5 minutes of start time or in progress/completed)
  const isMeetLinkAccessible = (startTimeStr) => {
    if (!startTimeStr) return false;
    const now = new Date();
    const start = new Date(startTimeStr);
    const fiveMinsBefore = new Date(start.getTime() - 5 * 60 * 1000);
    return now >= fiveMinsBefore;
  };

  // Filter Logic
  useEffect(() => {
    let filtered = [...schedules];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.class_title?.toLowerCase().includes(term) ||
          item.class_description?.toLowerCase().includes(term)
      );
    }

    if (courseFilter !== "all") {
      filtered = filtered.filter((item) => Number(item.course_id) === Number(courseFilter));
    }

    setFilteredSchedules(filtered);
    setCurrentPage(1);
  }, [searchTerm, courseFilter, schedules]);

  // Pagination calculation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSchedules.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSchedules.length / itemsPerPage);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    setEditMode(false);
    setSelectedScheduleId(null);
    setFormData({
      course_id: "",
      class_title: "",
      class_description: "",
      start_date: "",
      start_time: "",
      end_time: "",
      google_meet_link: ""
    });
    setCourseSearch("");
    setCourseDropdownOpen(false);
    setFormError("");
    setFormSuccess("");
    setIsCourseLocked(false);
    setExistingScheduleWarning(false);
    setModalOpen(true);
  };

  const openEditModal = (schedule) => {
    setEditMode(true);
    setSelectedScheduleId(schedule.id);
    setFormData({
      course_id: schedule.course_id,
      class_title: schedule.class_title,
      class_description: schedule.class_description || "",
      start_date: getYYYYMMDD(schedule.start_time),
      start_time: getHHMM(schedule.start_time),
      end_time: getHHMM(schedule.end_time),
      google_meet_link: schedule.google_meet_link || ""
    });
    setCourseSearch("");
    setCourseDropdownOpen(false);
    setFormError("");
    setFormSuccess("");
    setIsCourseLocked(true);
    setExistingScheduleWarning(false);
    setModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    // Basic Validation
    if (
      !formData.course_id ||
      !formData.class_title ||
      !formData.start_date ||
      !formData.start_time ||
      !formData.end_time ||
      (!isGoogleLinked && !formData.google_meet_link)
    ) {
      setFormError("Please fill out all required fields.");
      return;
    }

    // Google Meet URL validation
    if (formData.google_meet_link && !formData.google_meet_link.startsWith("http://") && !formData.google_meet_link.startsWith("https://")) {
      setFormError("Please enter a valid Google Meet URL starting with http:// or https://");
      return;
    }

    setFormLoading(true);
    try {
      // Parse local inputs to timezone-safe UTC ISO strings
      const parseLocalTime = (dateStr, timeStr) => {
        const [year, month, day] = dateStr.split("-").map(Number);
        const [hours, minutes] = timeStr.split(":").map(Number);
        return new Date(year, month - 1, day, hours || 0, minutes || 0);
      };

      const startDateTime = parseLocalTime(formData.start_date, formData.start_time);
      const endDateTime = parseLocalTime(formData.start_date, formData.end_time);

      const payload = {
        ...formData,
        start_date: startDateTime.toISOString().split("T")[0],
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        instructor_id: instructorId
      };

      let response;
      if (editMode) {
        response = await axiosInstance.put(`/schedule/${selectedScheduleId}`, payload);
      } else {
        response = await axiosInstance.post("/schedule", payload);
      }

      if (response.data && response.data.success) {
        setFormSuccess(editMode ? "Class updated successfully!" : "Class scheduled successfully!");
        fetchSchedules();
        setTimeout(() => {
          setModalOpen(false);
        }, 1500);
      } else {
        setFormError(response.data.message || "Failed to save schedule.");
      }
    } catch (err) {
      console.error("Error saving live class:", err);
      setFormError(err.response?.data?.message || "An error occurred while saving the live class.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClass = async (id) => {
    if (window.confirm("Are you sure you want to delete this live class schedule? This will remove the class and cannot be undone.")) {
      try {
        const response = await axiosInstance.delete(`/schedule/${id}`);
        if (response.data && response.data.success) {
          alert("Live class deleted successfully!");
          fetchSchedules();
        } else {
          alert(response.data.message || "Failed to delete schedule.");
        }
      } catch (err) {
        console.error("Error deleting schedule:", err);
        alert("Failed to delete live class.");
      }
    }
  };

  const getCourseName = (courseId) => {
    const course = courses.find((c) => Number(c.ID) === Number(courseId));
    return course ? course.CourseName : `Course #${courseId}`;
  };

  return (
    <div className="p-6 bg-slate-50/50 min-h-screen">
      {/* Header Banner */}
      <div className="mb-8 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-slate-500/10 rounded-full blur-2xl pointer-events-none" />
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 relative z-10">
          <div>
            <div className="flex items-center gap-3.5 mb-2.5">
              <div className="w-12 h-12 bg-white/10 border border-white/15 rounded-2xl flex items-center justify-center shadow-inner">
                <FaCalendarAlt className="text-indigo-400 text-2xl" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Live Classes Manager</h1>
                <p className="text-slate-400 text-xs sm:text-sm font-medium mt-0.5">Schedule and configure live Google Meet sessions for students.</p>
              </div>
            </div>
          </div>

          <button
            onClick={openCreateModal}
            className="bg-white hover:bg-slate-50 text-slate-900 font-extrabold text-sm px-6 py-3.5 rounded-2xl flex items-center gap-2.5 shadow-lg active:scale-95 transition-all cursor-pointer"
          >
            <FaPlus className="text-xs" />
            Schedule Live Class
          </button>
      </div>
    </div>

      {/* Google Calendar Link Banner */}
      {!isGoogleLinked ? (
        <div className="mb-6 bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-950 border border-indigo-500/20 rounded-3xl p-5 shadow-md flex flex-col md:flex-row items-center justify-between gap-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />
          <div className="flex items-center gap-4 relative z-10">
            <div className="w-12 h-12 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center shadow-inner shrink-0">
              <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h4 className="text-white font-extrabold text-sm sm:text-base">Connect Google Calendar</h4>
              <p className="text-slate-400 text-xs mt-0.5 max-w-xl font-medium">
                Link your account to auto-generate official Google Meet video links when scheduling classes, and invite students automatically.
              </p>
            </div>
          </div>
          <button
            onClick={handleLinkGoogle}
            disabled={googleLoading}
            className="bg-white hover:bg-slate-100 text-slate-900 text-xs font-black px-5 py-3.5 rounded-xl transition-all shadow-md active:scale-95 whitespace-nowrap cursor-pointer relative z-10"
          >
            {googleLoading ? "Connecting..." : "Link Calendar"}
          </button>
        </div>
      ) : (
        <div className="mb-6 bg-emerald-500/5 border border-emerald-500/10 rounded-3xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-500/10 rounded-xl flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-slate-700 text-xs sm:text-sm font-bold">Google Calendar Connected</p>
              <p className="text-slate-400 text-[11px] font-semibold mt-0.5">
                Official Google Meet links will automatically generate for new class schedules.
              </p>
            </div>
          </div>
          <button
            onClick={handleLinkGoogle}
            className="text-slate-500 hover:text-indigo-600 text-xs font-bold transition-all px-3 py-1.5 rounded-lg hover:bg-slate-100 cursor-pointer"
          >
            Reconnect Account
          </button>
        </div>
      )}

      {/* Toolbar / Search & Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4 bg-white p-4 rounded-3xl border border-slate-200 shadow-sm mb-6">
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
          <input
            type="text"
            placeholder="Search class title or description..."
            className="w-full pl-11 pr-4 py-3 text-sm bg-slate-50/50 border border-slate-200 rounded-2xl placeholder:text-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:bg-white transition-all font-semibold"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-3 justify-end flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider">Course Filter</span>
            <select
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              className="bg-white border border-slate-200 rounded-2xl px-4 py-3 text-xs sm:text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100"
            >
              <option value="all">All Courses</option>
              {courses.map((course) => (
                <option key={course.ID} value={course.ID}>
                  {course.CourseName}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={fetchSchedules}
            className="p-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-500 hover:text-slate-800 transition-colors shadow-2xs cursor-pointer"
            title="Refresh Schedules"
          >
            <FaSyncAlt className="text-sm" />
          </button>
        </div>
      </div>

      {/* Grid View of Schedules */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-slate-200 shadow-sm">
          <Loading />
        </div>
      ) : filteredSchedules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-slate-200 shadow-sm text-center px-6">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 text-3xl mb-4">
            <FaVideo className="text-slate-300" />
          </div>
          <h3 className="text-lg font-black text-slate-800">No Schedules Set</h3>
          <p className="text-slate-400 max-w-sm mt-1.5 text-sm font-medium">Schedule your first Google Meet class session by clicking the button above.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {currentItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-3xl border border-slate-200 shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col justify-between group overflow-hidden"
              >
                <div className="p-6">
                  {/* Top Row */}
                  <div className="flex justify-between items-start gap-2 mb-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase bg-slate-50 border border-slate-150 px-2.5 py-1 rounded-lg truncate max-w-[70%]">
                      {getCourseName(item.course_id)}
                    </span>
                    {isMeetLinkAccessible(item.start_time) ? (
                      <a
                        href={item.google_meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 shrink-0"
                      >
                        Meet <FaExternalLinkAlt className="text-[10px]" />
                      </a>
                    ) : (
                      <span 
                        className="text-xs font-bold text-slate-400 flex items-center gap-1.5 shrink-0 cursor-not-allowed"
                        title="Link accessible 5 mins before class"
                      >
                        Meet Locked
                      </span>
                    )}
                  </div>

                  {/* Title & Description */}
                  <h3 className="font-black text-slate-800 text-base leading-snug line-clamp-2 mt-2 group-hover:text-indigo-600 transition-colors">
                    {item.class_title}
                  </h3>
                  <p className="text-xs text-slate-500 font-medium line-clamp-3 mt-2 mb-4 leading-relaxed bg-slate-50/50 p-2.5 rounded-xl">
                    {item.class_description || "No description provided."}
                  </p>

                  <div className="border-t border-slate-100 my-4" />

                  {/* Timing info */}
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2.5 text-xs sm:text-sm font-semibold text-slate-600">
                      <FaCalendarAlt className="text-slate-400 shrink-0" />
                      <span>{formatDate(item.start_time)}</span>
                    </div>
                    <div className="flex items-center gap-2.5 text-xs sm:text-sm font-semibold text-slate-600">
                      <FaClock className="text-slate-400 shrink-0" />
                      <span>
                        {formatTime(item.start_time)} – {formatTime(item.end_time)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Card Actions */}
                <div className="px-6 pb-6 pt-2 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between gap-3">
                  <button
                    onClick={() => openEditModal(item)}
                    className="flex-1 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-2xs active:scale-95 transition-all cursor-pointer"
                  >
                    <FaEdit className="text-slate-400" /> Edit
                  </button>
                  <button
                    onClick={() => handleDeleteClass(item.id)}
                    className="p-2.5 bg-white hover:bg-rose-50 text-rose-600 border border-slate-200 hover:border-rose-100 rounded-xl shadow-2xs active:scale-95 transition-all cursor-pointer"
                    title="Delete Class"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex flex-col sm:flex-row justify-between items-center bg-white px-6 py-4 rounded-3xl border border-slate-200 shadow-sm gap-4">
              <span className="text-xs sm:text-sm text-slate-500 font-semibold">
                Showing{" "}
                <span className="font-extrabold text-slate-800">
                  {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, filteredSchedules.length)}
                </span>{" "}
                of <span className="font-extrabold text-slate-800">{filteredSchedules.length}</span> class schedules
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-slate-200"
                >
                  <FaChevronLeft size={10} />
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-10 h-10 text-xs sm:text-sm font-bold rounded-xl transition-all cursor-pointer ${
                      currentPage === i + 1
                        ? "bg-slate-900 text-white shadow-md"
                        : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all border border-slate-200"
                >
                  <FaChevronRight size={10} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Schedule / Edit Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md">
        <div className="flex flex-col">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-slate-900 to-indigo-950 px-6 py-5 rounded-t-3xl relative overflow-hidden flex items-center justify-between">
            <div>
              <h2 className="text-lg sm:text-xl font-black text-white">
                {editMode ? "Edit Live Class" : "Schedule Live Class"}
              </h2>
              <p className="text-slate-400 text-xs font-semibold mt-0.5">
                {editMode ? "Update details for this scheduled live session." : "Create and broadcast a new live class."}
              </p>
            </div>
            <button
              onClick={() => setModalOpen(false)}
              className="text-slate-400 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all cursor-pointer"
            >
              <FaTimes size={12} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleFormSubmit} className="p-6 bg-white rounded-b-3xl">
            {formError && (
              <div className="mb-4 p-3.5 bg-rose-50 border border-rose-100 text-rose-800 text-xs font-bold rounded-xl flex items-center gap-2">
                <FaExclamationCircle className="shrink-0" />
                <span>{formError}</span>
              </div>
            )}
            {formSuccess && (
              <div className="mb-4 p-3.5 bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs font-bold rounded-xl flex items-center gap-2">
                <span>{formSuccess}</span>
              </div>
            )}
            {existingScheduleWarning && (
              <div className="mb-4 p-3.5 bg-amber-50 border border-amber-100 text-amber-800 text-xs font-bold rounded-xl flex items-center gap-2">
                <FaExclamationCircle className="shrink-0 text-amber-500" />
                <span>An active schedule already exists for this course. Saving will update the existing schedule.</span>
              </div>
            )}

            <div className="space-y-4">
              {/* Course */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Select Course <span className="text-rose-500">*</span>
                </label>
                <div className="relative" ref={courseDropdownRef}>
                  <button
                    type="button"
                    onClick={() => !isCourseLocked && setCourseDropdownOpen((prev) => !prev)}
                    className={`w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-left flex items-center justify-between transition-all focus:outline-none focus:ring-2 focus:ring-slate-100 focus:bg-white ${isCourseLocked ? "opacity-60 cursor-not-allowed text-slate-500" : "cursor-pointer text-slate-700 hover:bg-white"}`}
                    disabled={isCourseLocked}
                  >
                    <span className={formData.course_id ? "text-slate-700" : "text-slate-400"}>
                      {formData.course_id ? getCourseName(formData.course_id) : "Select a course..."}
                    </span>
                    <FaChevronRight
                      className={`text-slate-400 text-xs transition-transform duration-200 ${courseDropdownOpen ? "rotate-90" : ""}`}
                    />
                  </button>

                  {courseDropdownOpen && !isCourseLocked && (
                    <div className="z-50 mt-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                      {/* Search input */}
                      <div className="p-2 border-b border-slate-100">
                        <div className="relative">
                          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs pointer-events-none" />
                          <input
                            type="text"
                            placeholder="Search courses..."
                            value={courseSearch}
                            onChange={(e) => setCourseSearch(e.target.value)}
                            autoFocus
                            className="w-full pl-8 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-200 focus:bg-white"
                          />
                        </div>
                      </div>

                      {/* Options list */}
                      <div className="max-h-52 overflow-y-auto">
                        {courses
                          .filter((c) =>
                            c.CourseName.toLowerCase().includes(courseSearch.toLowerCase())
                          )
                          .map((course) => (
                            <button
                              key={course.ID}
                              type="button"
                              onClick={() => {
                                const existing = schedules.find(
                                  (s) => Number(s.course_id) === Number(course.ID)
                                );
                                if (existing) {
                                  setEditMode(true);
                                  setSelectedScheduleId(existing.id);
                                  setFormData({
                                    course_id: course.ID,
                                    class_title: existing.class_title,
                                    class_description: existing.class_description || "",
                                    start_date: getYYYYMMDD(existing.start_time),
                                    start_time: getHHMM(existing.start_time),
                                    end_time: getHHMM(existing.end_time),
                                    google_meet_link: existing.google_meet_link || ""
                                  });
                                  setExistingScheduleWarning(true);
                                } else {
                                  setEditMode(false);
                                  setSelectedScheduleId(null);
                                  setFormData({
                                    course_id: course.ID,
                                    class_title: "",
                                    class_description: "",
                                    start_date: "",
                                    start_time: "",
                                    end_time: "",
                                    google_meet_link: ""
                                  });
                                  setExistingScheduleWarning(false);
                                }
                                setCourseDropdownOpen(false);
                                setCourseSearch("");
                              }}
                              className={`w-full text-left px-4 py-2.5 text-sm font-semibold transition-colors flex items-center gap-2 ${
                                Number(formData.course_id) === Number(course.ID)
                                  ? "bg-indigo-50 text-indigo-700"
                                  : "text-slate-700 hover:bg-slate-50"
                              }`}
                            >
                              <FaBookOpen className="text-slate-300 shrink-0 text-xs" />
                              {course.CourseName}
                            </button>
                          ))}

                        {courses.filter((c) =>
                          c.CourseName.toLowerCase().includes(courseSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-4 text-xs text-slate-400 font-medium text-center">
                            No courses found for "{courseSearch}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Class Title */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Class Title <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="class_title"
                  placeholder="e.g. Introduction to Neural Networks"
                  value={formData.class_title}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100 focus:bg-white transition-all"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Class Description
                </label>
                <textarea
                  name="class_description"
                  placeholder="Provide details about what topics will be covered..."
                  rows="3"
                  value={formData.class_description}
                  onChange={handleInputChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100 focus:bg-white transition-all resize-none"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                  Class Date <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100 focus:bg-white transition-all"
                />
              </div>

              {/* Start & End Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    Start Time <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100 focus:bg-white transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                    End Time <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100 focus:bg-white transition-all"
                  />
                </div>
              </div>

              {/* Google Meet Link */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Google Meet Link {isGoogleLinked ? <span className="text-[10px] text-emerald-600 font-bold lowercase italic">(optional - auto-generated)</span> : <span className="text-rose-500">*</span>}
                  </label>
                  <div className="flex gap-2 text-[11px] font-bold">
                    <button
                      type="button"
                      onClick={() => {
                        const letters = "abcdefghijklmnopqrstuvwxyz";
                        const genSeg = (len) => Array.from({ length: len }, () => letters[Math.floor(Math.random() * 26)]).join("");
                        const generated = `https://meet.google.com/${genSeg(3)}-${genSeg(4)}-${genSeg(3)}`;
                        setFormData(prev => ({ ...prev, google_meet_link: generated }));
                      }}
                      className="text-indigo-600 hover:text-indigo-800 transition-colors cursor-pointer"
                    >
                      Generate Auto Link
                    </button>
                    <span className="text-slate-300">|</span>
                    <a
                      href="https://meet.new"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-0.5"
                    >
                      Create Official Meet <FaExternalLinkAlt className="text-[9px]" />
                    </a>
                  </div>
                </div>
                <input
                  type="url"
                  name="google_meet_link"
                  placeholder={isGoogleLinked ? "Leave blank to auto-generate a Google Meet link, or paste a manual URL" : "https://meet.google.com/abc-defg-hij"}
                  value={formData.google_meet_link}
                  onChange={handleInputChange}
                  required={!isGoogleLinked}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100 focus:bg-white transition-all"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 px-5 py-3 rounded-xl text-sm font-bold active:scale-95 transition-all cursor-pointer"
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-slate-900 hover:bg-black text-white px-6 py-3 rounded-xl text-sm font-bold shadow-md active:scale-95 transition-all flex items-center gap-2 cursor-pointer"
                disabled={formLoading}
              >
                {formLoading ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : editMode ? (
                  "Update Class"
                ) : (
                  "Broadcast & Schedule"
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default InstructorClasses;
